import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import NTSearchAndFilter from '../ReportTable/NTSearchAndFilter';
import { RightPanelContainer } from './RightPanel/RightPanelContainer';
import { RightPanelProvider } from './RightPanel/RightPanelContext';

import NTLayout from './NTLayout';
import NTMiniMap from './NTMiniMap';
import NTColumnBrowser from './NTColumnBrowser';
import NTSkeleton from './NTSkeleton';
import { useNTUtilsContext } from './NTUtilsContext';
import { useNTTableContext } from './NTTableContext';
import { useNTSelectionContext } from './NTSelectionContext';
import { NTScrollBar } from './NTScrollBar';
import { useTableState } from './hooks/useTableState';
import { overflowShadowing } from './utils/sizingUtils';
import { useNTScroll } from './NTScrollContext';
import { useAuthContext } from 'context/useAuthContext';
import type { AskRequest } from '@hone-automation/common';
import { QueueContextProvider } from '../Queue/QueueContext';
import { useActiveReportStore } from 'hooks/useActiveReportStore';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
// useFlags moved to context to prevent multiple subscriptions
import AIPromptEditor from '../AIEditor/AIPromptEditor';
import { useReportsStore } from 'hooks/useReportsStore';
import { SUMMARY_PROMPT_TEMPLATE } from '../RightPanelModules/AIAnalysis/prompts';
import { logger } from '../KSExport/reportNTParser/utils';
import { getShowTotal, getShowDifference, getQueueCells, getAIAnalysis, isDateBetween } from '@hone-automation/common';
import { HoneReportTypes } from '../../../constants';

type NTMainLayoutProps = {
  parentRef: RefObject<HTMLDivElement>;
  transformedData: any;
  aiData: any;
  loading: boolean;
  searchTerm: string;
  fixFirstColumn: boolean;
  onSearch: (value: string) => void;
  onClear: () => void;
};

export const NTMainLayout = ({
  parentRef,
  transformedData,
  aiData,
  loading,
  searchTerm,
  fixFirstColumn,
  onSearch,
  onClear,
}: NTMainLayoutProps) => {
  const { actions, capabilities } = useNTUtilsContext();

  const { user } = useAuthContext();
  const reportPayload = useActiveReportStore(state => state.reportPayload);
  const { tableWidth, setTableWidth, setRedimParent, isExpanded } = useNTTableContext();
  const { cellStates, handleColumnSelect, selectRow, updateSelectionHistory, setSelectionHistory } =
    useNTSelectionContext();
  // Get the feature flag from context (prevents additional LaunchDarkly subscription)
  const { featureFlags } = useNTUtilsContext();
  const ldEnableAIEditor = featureFlags.enableAIEditor;
  // Temporary override for local testing - remove before production
  const enableAIEditor = true; // ldEnableAIEditor;
  const { aiReport, aiEditor, reportQueue } = useReportsStore();
  const [aiEditorVisible, setAiEditorVisible] = useState<boolean>(aiEditor);
  const [isAIEditorVisible, setIsAIEditorVisible] = useState(false);
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(false);
  const [isAIEditorConstrained, setIsAIEditorConstrained] = useState(false);
  const { scrollState, setScrollState } = useNTScroll();

  // Queue requests for QueueContextProvider (lifted from ReportQueueModule)
  const meId = user?.uid;
  const { data: liveQueueData } = useQueueRequestsQuery();
  const queueRequests = useMemo(() => {
    const fallbackRequests = getQueueCells(transformedData) || [];
    if (!liveQueueData || liveQueueData.length === 0) {
      return fallbackRequests;
    }

    const activeIssues = liveQueueData.filter((issue: AskRequest) => {
      const t = issue.type;
      const isValidType = t === 'Flagged Cell' || t === 'Ask Bookkeeper' || !t;
      return isValidType && !issue.deleted;
    });

    const reportType = transformedData?.type;
    if (reportType === HoneReportTypes.PLComparison) {
      const columnDates: Array<{ start: string; end: string }> = (transformedData?.columns || [])
        .filter((col: any) => col.date?.start && col.date?.end)
        .map((col: any) => col.date as { start: string; end: string });

      if (columnDates.length > 0) {
        return activeIssues.filter((issue: AskRequest) => {
          const trxDate = issue.transaction?.date;
          const issuePeriod = trxDate ? String(trxDate).split('T')[0] : issue.period;
          if (!issuePeriod) return false;
          return columnDates.some(d => isDateBetween(d.start, d.end, issuePeriod));
        });
      }
    }

    return activeIssues;
  }, [liveQueueData, transformedData]);

  const redimParentRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const prefix = prefixClass('whole-table');

  const reportIncludeFixedColumn = getShowTotal(transformedData);
  const showDifference = getShowDifference(transformedData);

  const getViewportWidth = useCallback(() => {
    const parentWidth = parentRef.current?.getBoundingClientRect().width || 0;
    return parentWidth > 0 ? parentWidth : window.innerWidth;
  }, [parentRef]);

  const getSmartTableWidth = useCallback(() => {
    const viewportWidth = getViewportWidth();
    const minPanelWidth = 260;
    const maxPanelWidth = 460;
    const preferredPanelWidth = Math.round(viewportWidth * 0.32);
    const minLeftWidth = 780;

    let panelWidth = Math.min(Math.max(preferredPanelWidth, minPanelWidth), maxPanelWidth);

    if (viewportWidth - panelWidth < minLeftWidth) {
      panelWidth = Math.max(minPanelWidth, viewportWidth - minLeftWidth);
    }

    const leftWidth = Math.max(0, Math.round(viewportWidth - panelWidth));
    return `${leftWidth}px`;
  }, [getViewportWidth]);

  const getWidthFromURLParam = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const tableWidthParam = params.get('tableWidth');
    if (!tableWidthParam) return null;

    const viewportWidth = getViewportWidth();
    const minLeft = Math.max(620, Math.round(viewportWidth * 0.52));
    const maxLeft = Math.max(minLeft, viewportWidth - 260);

    if (tableWidthParam.endsWith('px')) {
      const parsed = parseFloat(tableWidthParam);
      if (Number.isFinite(parsed)) {
        const clamped = Math.min(Math.max(parsed, minLeft), maxLeft);
        return `${Math.round(clamped)}px`;
      }
      return null;
    }

    if (tableWidthParam.endsWith('%')) {
      const parsed = parseFloat(tableWidthParam);
      if (Number.isFinite(parsed)) {
        const clamped = Math.min(Math.max(parsed, 55), 85);
        return `${clamped}%`;
      }
      return null;
    }

    return null;
  }, [getViewportWidth]);

  const getConstrainedTableWidth = useCallback(
    (useURLWidth: boolean = true) => {
      if (reportQueue) return 'calc(60vw)';
      if (useURLWidth) {
        const widthFromURL = getWidthFromURLParam();
        if (widthFromURL) return widthFromURL;
      }
      return getSmartTableWidth();
    },
    [reportQueue, getWidthFromURLParam, getSmartTableWidth]
  );

  useTableState();

  useEffect(() => {
    setAiEditorVisible(aiEditor);
  }, [aiEditor]);

  useEffect(() => {
    const shouldShowPanel = (actions.detailsPanel && capabilities.charts) || aiReport || reportQueue;
    if (shouldShowPanel) {
      setTableWidth(getConstrainedTableWidth(true));
    } else {
      setTableWidth('100%');
    }
    setIsRightPanelVisible(shouldShowPanel);
  }, [actions.detailsPanel, capabilities.charts, aiReport, reportQueue, setTableWidth, getConstrainedTableWidth]);

  // Adjust width when panel visibility or expansion toggles
  useEffect(() => {
    if (aiEditorVisible) {
      setTableWidth('700px');
      return;
    }
    if (isRightPanelVisible) {
      if (isExpanded) {
        // already handled by useTableState on expand
        return;
      }
      setTableWidth(getConstrainedTableWidth(false));
    } else {
      setTableWidth('100%');
    }
  }, [isRightPanelVisible, isExpanded, aiEditorVisible, reportQueue, setTableWidth, getConstrainedTableWidth]);

  useEffect(() => {
    if (!transformedData || !transformedData.columns.length) return;
    //console.log('enter NTMainLayout first entry point');
    setRedimParent(isRightPanelVisible ? redimParentRef.current : null);
    //setScrollState({ ...scrollState, columnBrowserVisible: overflowShadowing() });
    overflowShadowing();
  }, [isRightPanelVisible, transformedData.columns.length]);

  useEffect(() => {
    // Batch state updates on data change - use queueMicrotask to avoid setState-during-render warnings
    // This ensures scroll operations run after the current render cycle completes
    queueMicrotask(() => {
      setSelectionHistory([]);
      if (cellStates.selected) {
        updateSelectionHistory(cellStates.selected);
        selectRow(true);
      }
      if (cellStates.selectedColumn !== null && cellStates.selectedColumn !== undefined) {
        handleColumnSelect(cellStates.selectedColumn, true);
      }
    });
  }, [transformedData.columns.length]);

  const { setAiEditor } = useReportsStore(state => ({ setAiEditor: state.setAiEditor }));

  const onCloseEditor = () => {
    setAiEditorVisible(false);
  };

  const handleConstrainChange = (isConstrained: boolean) => {
    setIsAIEditorConstrained(isConstrained);
  };

  const onResponse = (response: any, error: any) => {
    logger.msg('error', error);
    logger.msg('response', response);
  };

  const MemoizedComponents = useMemo(() => {
    return {
      miniMap: <NTMiniMap rawData={transformedData} />,
      scrollBar: <NTScrollBar />,
      columnBrowser: <NTColumnBrowser />,
    };
  }, [transformedData.columns.length]);

  const MemoizedAIEditor = useMemo(() => {
    const aiAnalysis = getAIAnalysis(transformedData);
    const dataArray = aiAnalysis?.data.hierarchicalData
      ? [aiAnalysis?.data.hierarchicalData]
      : Array.isArray(aiAnalysis?.data)
        ? aiAnalysis?.data
        : [aiAnalysis?.data];

    const username = (user?.displayName || user?.email?.split('@')[0].split('.')[0] || 'User')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const variableData = {
      data: dataArray,
      statistics: aiAnalysis?.statistics,
      significantChanges: aiAnalysis?.significantChanges,
      period: aiAnalysis?.timeframe,
      username: username,
    };
    const prompt = SUMMARY_PROMPT_TEMPLATE;
    return (
      <AIPromptEditor
        onClose={onCloseEditor}
        variableData={variableData}
        initialPrompt={prompt}
        onResponse={onResponse}
        reportData={transformedData}
        onConstrainChange={handleConstrainChange}
      />
    );
  }, [transformedData]);

  // Memoize the RightPanelContainer to optimize rendering performance
  // Only re-render when the actual report ID or aiData changes, not when transformedData object reference changes
  const MemoizedRightPanel = useMemo(() => {
    return <RightPanelContainer rawData={transformedData} aiData={aiData} />;
  }, [reportPayload?.id, transformedData.columns.length, aiData, getQueueCells(transformedData)]);

  const MemoizedTable = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const comparisonPeriodsFlag = urlParams.get('comparisonPeriods');
    const comparisonPeriods = comparisonPeriodsFlag ? parseInt(comparisonPeriodsFlag) : 1;
    if (loading || !transformedData || !transformedData.columns.length) {
      return <NTSkeleton columns={comparisonPeriods + 1} rows={40} />;
    }
    return (
      <NTLayout
        parentRef={parentRef}
        key="myReportTable"
        data={transformedData}
        fixFC={fixFirstColumn}
        fixLC={actions.total || reportIncludeFixedColumn || actions.difference || showDifference}
        searchTerm={searchTerm}
      />
    );
  }, [loading, transformedData, transformedData.columns.length, getQueueCells(transformedData), searchTerm]);

  const mainContent = (
    <RightPanelProvider>
      <div
        className={classNames(prefix(), {
          'right-pane-visible': actions.detailsPanel && capabilities.charts,
        })}
      >
        <div
          className={classNames('left-pane', { 'lp-rp-visible': actions.detailsPanel && capabilities.charts })}
          ref={redimParentRef}
          style={{ width: tableWidth, flex: `0 0 ${tableWidth}` }}
        >
          <div className="table-search-wrapper">
            <NTSearchAndFilter
              selectedReportType={transformedData?.type}
              searchValue={searchTerm}
              onSearch={onSearch}
              onClear={onClear}
            />
            <div className="new-table-wrapper">
              <div className="new-table" id="new-table" style={{ overflowX: isExpanded ? 'hidden' : undefined }}>
                {MemoizedTable}
              </div>
              <div
                className={classNames('new-table-wrapper-right-shadow-element', {
                  'lp-rp-visible': actions.detailsPanel && capabilities.charts,
                })}
              >
                {' '}
              </div>
            </div>
            {!isExpanded && MemoizedComponents.columnBrowser}
          </div>
          {MemoizedComponents[!isRightPanelVisible ? 'miniMap' : 'scrollBar']}
        </div>
        <div
          ref={rightPaneRef}
          className={classNames('right-pane', {
            'rp-visible': actions.detailsPanel,
            'rp-report-queue': reportQueue,
            'rp-expanded': isExpanded,
          })}
        >
          {isRightPanelVisible && MemoizedRightPanel}
        </div>
        <div className="ai-editor-container">
          {enableAIEditor && (
            <div
              className={classNames('ai-editor-container-area', {
                'ai-editor-visible': aiEditorVisible,
                'ai-editor-constrained': isAIEditorConstrained,
              })}
            >
              {MemoizedAIEditor}
            </div>
          )}
        </div>
      </div>
    </RightPanelProvider>
  );

  return (
    <QueueContextProvider meId={meId} requests={queueRequests} syncToUrl={false}>
      {mainContent}
    </QueueContextProvider>
  );
};
